import { Box } from '@mantine/core';
import { useEffect, type FC, type ReactNode } from 'react';
import { AppSidebar } from './AppSidebar';
import classes from './AppSidebar.module.css';
import { useProjectSidebarVisible } from './useProjectSidebarVisible';

type Props = {
    isNavBarFixed: boolean;
    children: ReactNode;
};

/** Renders the project sidebar and shifts the page clear of it. */
export const ProjectSidebarFrame: FC<Props> = ({ isNavBarFixed, children }) => {
    const projectUuid = useProjectSidebarVisible(true);

    // A static navbar (dashboard views) scrolls away with the page, so keep
    // the sidebar under whatever part of it is still on screen. DOM sync.
    useEffect(() => {
        if (!projectUuid || isNavBarFixed) return undefined;
        const root = document.documentElement;
        let frame = 0;
        const update = () => {
            frame = 0;
            const header = document.getElementById('navbar-header');
            const bottom = header
                ? Math.max(0, header.getBoundingClientRect().bottom)
                : 0;
            root.style.setProperty('--app-sidebar-top', `${bottom}px`);
        };
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update);
        };
        update();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        return () => {
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            if (frame) cancelAnimationFrame(frame);
            root.style.removeProperty('--app-sidebar-top');
        };
    }, [projectUuid, isNavBarFixed]);

    if (!projectUuid) return <>{children}</>;

    return (
        <>
            <AppSidebar projectUuid={projectUuid} />
            {/* The rounded joint only makes sense under a navbar that stays. */}
            {isNavBarFixed && <Box className={classes.corner} aria-hidden />}
            <Box className={classes.withSidebar}>{children}</Box>
        </>
    );
};
